/* eslint-disable complexity */
import { useMediaQuery, useTheme } from '@material-ui/core'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import { InfoButton } from 'components/buttons'
import classNames from 'classnames'
import { useTracking } from 'lib/analytics'
import { TextSection as TextSectionType } from '../../LandingPage.types'
import css from './TextSection.styles.scss'
import { ScreenSize, useConfetti } from './TextSection.utils'
import Arch from './svgs/Arch'
import Circle from './svgs/Circle'
import Rainbow from './svgs/Rainbow'
import RoundedTriangle from './svgs/RoundedTriangle'

interface TextSectionProps {
  section: TextSectionType
}

const TextSection = ({ section }: TextSectionProps) => {
  const [currentUser] = useCurrentUser()
  const tracker = useTracking()
  const {
    heading,
    isSectionPageHeader = false,
    subHeading,
    body,
    callToAction,
    includeVerticalSectionSpacing,
    background,
    desktopWidth,
    sectionAlignment = 'center',
    useWaveBorders = false,
    mobileOnly = false,
  } = section
  const isFullWidthSection = desktopWidth === 'full'
  const includeLargeBackgroundShapes = background === 'blueToPurple'
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))
  let screenSize: ScreenSize = 'medium'
  if (isSmallScreen) screenSize = 'small'
  if (isLargeScreen) screenSize = 'large'
  const hasHeading = heading !== undefined || subHeading !== undefined

  if (!isSmallScreen && mobileOnly) {
    return null
  }

  const confetti = useConfetti({ size: screenSize })

  const conditionalContainerClasses = {
    [css['TextSection__container--leftAligned']]: sectionAlignment === 'left',
    [css['TextSection__container--rightAligned']]: sectionAlignment === 'right',
    [css['TextSection__container--centerAligned']]:
      sectionAlignment === 'center',
    [css['TextSection__container--fullWidthSection']]: isFullWidthSection,
    [css['TextSection__container--includeVerticalSectionSpacing']]:
      includeVerticalSectionSpacing,
    [css['TextSection__container--contrastBackground']]:
      background === 'confetti',
    [css['TextSection__container--blueToPurpleBackground']]:
      background === 'blueToPurple',
    [css['TextSection__container--waveBorders']]: useWaveBorders,
  }

  return (
    <div
      className={classNames(
        css.TextSection__container,
        conditionalContainerClasses
      )}
    >
      <div className={css.TextSection__ctaGap}>
        <div className={css.TextSection__bodyGap}>
          {hasHeading && (
            <>
              {isSectionPageHeader ? (
                <div className={css.TextSection__subHeadingGap}>
                  {heading && (
                    <h1 className={css.TextSection__heading}>{heading}</h1>
                  )}
                  {subHeading && (
                    <h2 className={css.TextSection__subHeading}>
                      {subHeading}
                    </h2>
                  )}
                </div>
              ) : (
                <div className={css.TextSection__subHeadingGap}>
                  {heading && (
                    <h3 className={css.TextSection__heading}>{heading}</h3>
                  )}
                  {subHeading && (
                    <h4 className={css.TextSection__subHeading}>
                      {subHeading}
                    </h4>
                  )}
                </div>
              )}
            </>
          )}
          {renderBody(css.TextSection__body, body)}
        </div>
        {callToAction && (
          <InfoButton
            pill
            className={css.TextSection__callToAction}
            href={callToAction.url}
            onClick={() => {
              if (typeof callToAction.onClick === 'function') {
                callToAction?.onClick(currentUser, tracker)
              }
            }}
          >
            {callToAction.text}
          </InfoButton>
        )}
      </div>
      {background === 'confetti' && (
        <div className={css.TextSection__confetti}>
          {confetti.map(({ key, icon, styles }) => (
            <img alt="Confetti Icon" key={key} src={icon} style={styles} />
          ))}
        </div>
      )}
      {includeLargeBackgroundShapes && (
        <>
          <Arch
            className={classNames(
              css.TextSection__backgroundIcon,
              css['TextSection__backgroundIcon--arch']
            )}
          />
          <Circle
            className={classNames(
              css.TextSection__backgroundIcon,
              css['TextSection__backgroundIcon--circle']
            )}
          />
          <Rainbow
            className={classNames(
              css.TextSection__backgroundIcon,
              css['TextSection__backgroundIcon--rainbow']
            )}
          />
          <RoundedTriangle
            className={classNames(
              css.TextSection__backgroundIcon,
              css['TextSection__backgroundIcon--roundedTriangle']
            )}
          />
        </>
      )}
    </div>
  )
}

const renderBody = (className: string, body: string | undefined) => {
  if (!body) return null
  return <p className={className} dangerouslySetInnerHTML={{ __html: body }} />
}

export default TextSection
